/**
 * Appartenance d'une carte à un îlot (C28, spec § 2) : une carte appartient à l'îlot dont
 * le cadre contient son CENTRE ; si plusieurs cadres le contiennent, le plus petit gagne
 * (déterministe, un cadre dans un cadre reste lisible) ; sinon elle est hors îlot.
 * Rien ici n'est stocké : tout se calcule à la lecture. Pur, sans DOM.
 *
 * Ce module n'importe rien : le serveur en reçoit une COPIE octet pour octet
 * (`api/src/utils/appartenance.ts`, test de dérive `appartenance.drift.test.ts`) pour relire
 * la matière d'un cadre avec la même règle que l'écran (revue après merge du 19/10). Toute
 * modification ici se recopie là-bas. Les types sont structurels : `Carte` et `Ilot` de
 * `src/types/metanote.ts` les satisfont, comme les cartes normalisées du serveur.
 */

/** Taille nominale d'une carte sur le plateau (unités du plateau) : le centre s'en déduit. */
export const CARTE_TAILLE = Object.freeze({ largeur: 240, hauteur: 120 });

export interface Point { x: number; y: number }
/** Ce qu'une carte doit porter pour être placée : son coin haut-gauche et son identifiant. */
export interface CartePlacee { id: string; x: number; y: number }
/** Ce qu'un îlot doit porter pour contenir : un cadre, un identifiant et un rang. */
export interface Cadre { id: string; x: number; y: number; largeur: number; hauteur: number; rang: number }

/** Hauteur d'une bande de lecture : deux cartes à moins de 40 unités d'écart vertical sont sur la même ligne. */
export const BANDE_DE_LECTURE = 40;

export function centreDeLaCarte(carte: Point): Point {
  return { x: carte.x + CARTE_TAILLE.largeur / 2, y: carte.y + CARTE_TAILLE.hauteur / 2 };
}

export function contient(ilot: Omit<Cadre, 'id' | 'rang'>, point: Point): boolean {
  return point.x >= ilot.x && point.x <= ilot.x + ilot.largeur && point.y >= ilot.y && point.y <= ilot.y + ilot.hauteur;
}

const aire = (ilot: Omit<Cadre, 'id' | 'rang'>): number => ilot.largeur * ilot.hauteur;

export function ilotDeLaCarte<I extends Cadre>(carte: Point, ilots: readonly I[]): I | null {
  const centre = centreDeLaCarte(carte);
  let gagnant: I | null = null;
  for (const ilot of ilots) {
    if (!contient(ilot, centre)) continue;
    if (!gagnant || aire(ilot) < aire(gagnant)) gagnant = ilot;
  }
  return gagnant;
}

/** De haut en bas par bandes, puis de gauche à droite dans une bande (spec § 5.1, point P5). */
export function ordreDeLecture<C extends CartePlacee>(cartes: readonly C[]): C[] {
  const bande = (c: C) => Math.round(c.y / BANDE_DE_LECTURE);
  return [...cartes].sort((a, b) => bande(a) - bande(b) || a.x - b.x || a.id.localeCompare(b.id));
}

export function cartesParIlot<C extends CartePlacee, I extends Cadre>(cartes: readonly C[], ilots: readonly I[]): { parIlot: Map<string, C[]>; horsIlot: C[] } {
  const parIlot = new Map<string, C[]>(ilots.map(i => [i.id, [] as C[]]));
  const horsIlot: C[] = [];
  for (const carte of cartes) {
    const ilot = ilotDeLaCarte(carte, ilots);
    if (ilot) parIlot.get(ilot.id)!.push(carte);
    else horsIlot.push(carte);
  }
  for (const [id, liste] of parIlot) parIlot.set(id, ordreDeLecture(liste));
  return { parIlot, horsIlot: ordreDeLecture(horsIlot) };
}

export function ilotsOrdonnes<I extends Cadre>(ilots: readonly I[]): I[] {
  return [...ilots].sort((a, b) => a.rang - b.rang || a.id.localeCompare(b.id));
}

export function rangSuivant(ilots: readonly Pick<Cadre, 'rang'>[]): number {
  return ilots.length === 0 ? 0 : Math.max(...ilots.map(i => i.rang)) + 1;
}
